import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Loader2, ScrollText } from "lucide-react";
import { Consents } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * ConsentGate — blocking full-screen modal that renders when the logged-in
 * user has any pending consent documents (RODO, Regulamin, admin-defined
 * kinds). The user must tick a checkbox next to each doc before the
 * "Akceptuję" button unlocks. Acceptance is POSTed to `/api/consents/accept`;
 * on success the modal closes and the app continues normally.
 *
 * Renders `null` when there's nothing to accept — perfectly safe to mount
 * globally under the auth-protected layout tree.
 */
export default function ConsentGate() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [checked, setChecked] = useState({});
  const [expanded, setExpanded] = useState({}); // doc.id -> bool (long body expanded)
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Fetch pending consents whenever the user identity changes.
  useEffect(() => {
    if (!user) {
      setPending([]);
      setLoaded(false);
      return;
    }
    let cancelled = false;
    Consents.pending()
      .then((data) => {
        if (cancelled) return;
        setPending(data.pending || []);
        // Auto-expand short bodies for a smoother reading experience.
        const nextExpanded = {};
        (data.pending || []).forEach((d) => {
          if ((d.body || "").length < 400) nextExpanded[d.id] = true;
        });
        setExpanded(nextExpanded);
        setChecked({});
      })
      .catch(() => {
        // Silently no-op — endpoint may not be reachable yet.
      })
      .finally(() => !cancelled && setLoaded(true));
    return () => { cancelled = true; };
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user || !loaded || pending.length === 0) return null;

  const allChecked = pending.every((d) => checked[d.id]);

  const submit = async () => {
    if (!allChecked || busy) return;
    setBusy(true);
    try {
      const ids = pending.map((d) => d.id);
      await Consents.accept(ids);
      toast.success("Dziękujemy — zgody zostały zapisane.");
      setPending([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie mogę zapisać zgód");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1500] bg-zinc-950/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-6"
      data-testid="consent-gate"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-2xl max-h-[92vh] rounded-3xl bg-white shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative overflow-hidden px-5 py-5 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black text-white">
          <ShieldCheck className="absolute -right-6 -bottom-8 w-40 h-40 opacity-10" strokeWidth={1.5} />
          <div className="relative">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">
              // Wymagane zgody
            </div>
            <h2 className="font-heading font-black uppercase tracking-tight text-2xl sm:text-3xl mt-1">
              Zanim przejdziesz dalej
            </h2>
            <p className="text-sm text-white/80 mt-2 max-w-md">
              Zapoznaj się z poniższymi dokumentami i potwierdź ich akceptację —
              musisz to zrobić przy pierwszym logowaniu oraz po każdej istotnej
              aktualizacji.
            </p>
          </div>
        </div>

        {/* Body scroll */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3">
          {pending.map((doc) => (
            <ConsentDocCard
              key={doc.id}
              doc={doc}
              expanded={!!expanded[doc.id]}
              onToggleExpand={() =>
                setExpanded((p) => ({ ...p, [doc.id]: !p[doc.id] }))
              }
              checked={!!checked[doc.id]}
              onCheck={(v) => setChecked((p) => ({ ...p, [doc.id]: v }))}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-5 border-t border-zinc-100 bg-zinc-50 flex flex-col sm:flex-row items-center gap-3">
          <div className="flex-1 text-xs text-zinc-500 font-mono">
            {allChecked
              ? "// Wszystkie zgody zaznaczone — możesz kontynuować."
              : `// Zaznacz wszystkie wymagane zgody (${
                  pending.filter((d) => checked[d.id]).length
                }/${pending.length}).`}
          </div>
          <button
            data-testid="consent-gate-submit"
            disabled={!allChecked || busy}
            onClick={submit}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl font-heading font-black uppercase tracking-widest text-sm transition-all ${
              allChecked
                ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg active:scale-95"
                : "bg-zinc-200 text-zinc-400 cursor-not-allowed"
            }`}
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            {busy ? "Zapisuję…" : "Akceptuję"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConsentDocCard({ doc, expanded, onToggleExpand, checked, onCheck }) {
  const body = doc.body || "";
  const long = body.length >= 400;
  const shown = expanded ? body : body.slice(0, 400);
  return (
    <div
      data-testid={`consent-doc-${doc.kind}`}
      className={`rounded-2xl ring-1 transition-colors ${
        checked ? "ring-emerald-300 bg-emerald-50/50" : "ring-zinc-200 bg-white"
      }`}
    >
      <div className="p-4 flex items-start gap-3">
        <ScrollText className="w-4 h-4 text-zinc-500 mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              // {doc.kind} · v{doc.version}
            </span>
            {doc.require_reaccept && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-widest">
                Wymaga akceptacji
              </span>
            )}
          </div>
          <h3 className="font-heading font-black uppercase tracking-tight text-base sm:text-lg mt-1">
            {doc.title}
          </h3>
          <p className="mt-2 text-[13px] leading-relaxed text-zinc-700 whitespace-pre-wrap break-words">
            {shown}
            {long && !expanded && "…"}
          </p>
          {long && (
            <button
              type="button"
              data-testid={`consent-toggle-${doc.kind}`}
              onClick={onToggleExpand}
              className="mt-1 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 underline underline-offset-2"
            >
              {expanded ? "Zwiń" : "Rozwiń pełną treść"}
            </button>
          )}
        </div>
      </div>
      <label
        className={`flex items-center gap-3 px-4 py-3 border-t cursor-pointer select-none ${
          checked ? "border-emerald-200 bg-emerald-50" : "border-zinc-100 bg-zinc-50"
        }`}
      >
        <input
          type="checkbox"
          data-testid={`consent-check-${doc.kind}`}
          checked={checked}
          onChange={(e) => onCheck(e.target.checked)}
          className="w-5 h-5 accent-emerald-500 cursor-pointer"
        />
        <span className="text-sm font-semibold text-zinc-800">
          Zapoznałem/-am się z treścią i akceptuję ten dokument.
        </span>
      </label>
    </div>
  );
}
